import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, ChevronRight, ArrowLeft } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import Seo, { SITE_URL, SITE_NAME } from "../components/Seo";
import { useBlogs } from "../lib/BlogsContext";
import { optimizeImg } from "../lib/img";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso.replace(" ", "T")).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const { getBlogPost } = useBlogs();
  const [post, setPost] = useState(undefined); // undefined = yükleniyor, null = bulunamadı

  useEffect(() => {
    let cancelled = false;
    setPost(undefined);
    getBlogPost(slug).then((p) => {
      if (!cancelled) setPost(p);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, getBlogPost]);

  if (post === undefined) {
    return (
      <div className="min-h-screen bg-white text-[#222222]" data-testid="blog-post-loading">
        <Seo title="Yükleniyor…" noindex />
        <Header />
        <div className="frida-container py-32 text-center text-[#888888]">Yükleniyor…</div>
        <Footer />
        <FloatingButtons />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white text-[#222222]" data-testid="blog-post-not-found">
        <Seo title="Yazı Bulunamadı" noindex />
        <Header />
        <div className="frida-container py-32 text-center">
          <h1 className="font-serif text-3xl mb-4">Yazı bulunamadı</h1>
          <Link to="/blog" className="text-[#A85C34] font-medium hover:underline">
            Blog'a dön
          </Link>
        </div>
        <Footer />
        <FloatingButtons />
      </div>
    );
  }

  // İçerik düz metin olarak saklanır (panelde textarea) — paragraf aralarında
  // boş satır, tek satır sonu ise aynı paragraf içinde satır kırılması sayılır.
  const paragraphs = (post.content || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || post.excerpt || undefined,
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.published_at || undefined,
    dateModified: post.updated_at || post.published_at || undefined,
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: { "@type": "Organization", name: SITE_NAME },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: SITE_URL + "/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` },
    ],
  };

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="blog-post-page">
      <Seo
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt || undefined}
        path={`/blog/${post.slug}`}
        image={post.cover_image || undefined}
        type="article"
        jsonLd={[articleLd, breadcrumbLd]}
      />
      <Header />

      <div className="frida-container pt-6">
        <nav className="flex items-center gap-2 text-xs text-[#888888]" data-testid="blog-post-breadcrumb">
          <Link to="/" className="hover:text-[#A85C34]">Ana Sayfa</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-[#A85C34]">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#222222] truncate max-w-[220px]">{post.title}</span>
        </nav>
      </div>

      <article className="py-8 md:py-12">
        <div className="frida-container max-w-3xl mx-auto">
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#222222] tracking-tight leading-[1.15] mb-4" data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.published_at && (
            <div className="flex items-center gap-1.5 text-sm text-[#888888] mb-8">
              <CalendarDays className="w-4 h-4" />
              {formatDate(post.published_at)}
            </div>
          )}

          {post.cover_image && (
            <div className="aspect-video rounded-3xl overflow-hidden bg-[#F8F8F8] mb-10">
              <img
                src={optimizeImg(post.cover_image, 1000)}
                alt={post.title}
                decoding="async"
                fetchPriority="high"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="prose-frida space-y-5" data-testid="blog-post-content">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-[#333333] text-lg leading-relaxed whitespace-pre-line">
                {p}
              </p>
            ))}
          </div>

          <Link
            to="/blog"
            className="inline-flex items-center gap-2 mt-14 text-sm text-[#A85C34] font-medium hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Tüm Yazılara Dön
          </Link>
        </div>
      </article>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
